import { useState, type FormEvent } from 'react';
import { formatStorageSize, storagePercent } from '@bekebi/domain';
import type { ServiceMetric } from '@bekebi/contracts/api';
import { Button } from '../../components/button';
import { ErrorState, LoadingState } from '../../components/states';
import { useHasPermission } from '../../session/current-user';
import { useEnterPlanSize, useServiceMetrics } from './api';
import { Section, UnavailableNote } from './components';
import { formatDateTime, formatRelative } from './format';

/**
 * Storage Bucket space (docs/integrations/storage-bucket.md), written exactly as the storage
 * service's own dashboard writes it (director, 2026-11-02): "360 GB / 2 TB (17.6%)" over a bar,
 * and the share still free. Space used is measured hourly by the portal from a read-only listing
 * of the bucket; the plan size is not reported by the storage service, so the Admin enters it from
 * that dashboard and it is shown as entered, never as measured.
 */
export function StorageBucketUsage({ serviceId }: { serviceId: string }) {
  const query = useServiceMetrics(serviceId);
  const canManage = useHasPermission('services.manage');
  if (query.isPending) return <LoadingState label="Loading storage usage…" />;
  if (query.isError) {
    return (
      <ErrorState
        title="Storage usage could not be loaded"
        message={query.error.message}
        onRetry={() => void query.refetch()}
      />
    );
  }
  const { metrics, collectionIntervalMinutes } = query.data;
  const used = metrics.find((m) => m.key === 'storage.bucket.used_bytes');
  const plan = metrics.find((m) => m.key === 'storage.bucket.capacity_bytes');
  const every = collectionIntervalMinutes ? ` every ${collectionIntervalMinutes} minutes` : '';

  return (
    <Section title="Storage" description="Space used in the Storage Bucket against its plan.">
      <div className="space-y-4">
        {used ? (
          <StorageMeter used={used} plan={plan ?? null} />
        ) : (
          <UnavailableNote>
            Storage usage has not been measured yet. Once the Storage Bucket&apos;s read-only access
            is set up, it is measured{every}.
          </UnavailableNote>
        )}
        {plan ? (
          <p className="text-xs text-muted">
            Plan size {formatStorageSize(plan.value)}, entered by the Admin on{' '}
            {formatDateTime(plan.observedAt)} from the storage service&apos;s dashboard.
          </p>
        ) : null}
        {canManage ? <PlanSizeForm serviceId={serviceId} current={plan?.value ?? null} /> : null}
      </div>
    </Section>
  );
}

/** "360 GB / 2 TB (17.6%)", the bar, and what is left — the storage dashboard's own wording. */
export function StorageMeter({ used, plan }: { used: ServiceMetric; plan: ServiceMetric | null }) {
  const measured = (
    <p className="text-xs text-muted">
      {used.stale
        ? `Out of date: last measured ${formatRelative(used.observedAt)}.`
        : `Measured ${formatRelative(used.observedAt)}.`}
    </p>
  );
  if (!plan) {
    return (
      <div className="space-y-1">
        <p className="text-lg font-semibold text-fg">{formatStorageSize(used.value)} used</p>
        <p className="text-sm text-muted">The plan size has not been entered yet.</p>
        {measured}
      </div>
    );
  }
  const usedPercent = storagePercent(used.value, plan.value);
  const leftPercent = storagePercent(used.value, plan.value, true);
  const width = Math.min(100, Number(usedPercent));
  const nearlyFull = Number(usedPercent) >= 90;
  return (
    <div className="space-y-2">
      <div
        role="meter"
        aria-label="Storage Bucket space used"
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={width}
        aria-valuetext={`${formatStorageSize(used.value)} of ${formatStorageSize(plan.value)} used (${usedPercent}%)`}
        className="h-2 w-full max-w-md overflow-hidden rounded-full bg-raised"
      >
        <div
          className={`h-full rounded-full ${nearlyFull ? 'bg-warning' : 'bg-success'}`}
          style={{ width: `${width}%` }}
        />
      </div>
      <p className="text-base">
        <span className={`font-semibold ${nearlyFull ? 'text-warning' : 'text-success'}`}>
          {formatStorageSize(used.value)}
        </span>
        <span className="text-fg-2">
          {' '}
          / {formatStorageSize(plan.value)} ({usedPercent}%)
        </span>
      </p>
      <p className="text-sm text-fg-2">{leftPercent}% left</p>
      {measured}
    </div>
  );
}

const UNITS = { GB: 1024 ** 3, TB: 1024 ** 4 } as const;

/** The Admin enters the plan size as the storage dashboard shows it (e.g. 2 TB). */
function PlanSizeForm({ serviceId, current }: { serviceId: string; current: number | null }) {
  const enter = useEnterPlanSize(serviceId);
  const [amount, setAmount] = useState(current ? String(current / UNITS.TB) : '');
  const [unit, setUnit] = useState<keyof typeof UNITS>('TB');
  const [problem, setProblem] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  const onSubmit = (event: FormEvent) => {
    event.preventDefault();
    setSaved(false);
    const value = Number(amount);
    if (!/^\d+(\.\d{1,2})?$/.test(amount.trim()) || value <= 0) {
      setProblem('Enter the plan size as a number, for example 2.');
      return;
    }
    setProblem(null);
    enter.mutate(Math.round(value * UNITS[unit]), { onSuccess: () => setSaved(true) });
  };

  return (
    <form
      onSubmit={onSubmit}
      noValidate
      aria-label="Plan size"
      className="space-y-2 border-t border-line pt-4"
    >
      <label htmlFor="plan-size" className="block text-sm font-medium text-fg-2">
        Plan size (as shown on the storage service&apos;s dashboard)
      </label>
      <div className="flex flex-wrap items-center gap-2">
        <input
          id="plan-size"
          inputMode="decimal"
          value={amount}
          onChange={(e) => setAmount(e.target.value)}
          aria-invalid={problem ? 'true' : 'false'}
          className="w-28 rounded-md border border-line-strong bg-surface px-3 py-2 text-sm"
        />
        <select
          aria-label="Unit"
          value={unit}
          onChange={(e) => setUnit(e.target.value as keyof typeof UNITS)}
          className="rounded-md border border-line-strong bg-surface px-3 py-2 text-sm"
        >
          <option value="TB">TB</option>
          <option value="GB">GB</option>
        </select>
        <Button type="submit" disabled={enter.isPending}>
          {enter.isPending ? 'Saving…' : 'Save plan size'}
        </Button>
      </div>
      {problem ? <p className="text-sm text-danger">{problem}</p> : null}
      {enter.isError ? (
        <p role="alert" className="text-sm text-danger">
          {enter.error.message}
        </p>
      ) : null}
      {saved ? (
        <p role="status" className="text-sm text-success">
          The plan size was saved.
        </p>
      ) : null}
    </form>
  );
}

/** One line for lists: "360 GB / 2 TB (17.6%) · 82.4% left", or what is missing. */
export function StorageSummaryLine({ serviceId }: { serviceId: string }) {
  const query = useServiceMetrics(serviceId);
  if (!query.data) return null;
  const used = query.data.metrics.find((m) => m.key === 'storage.bucket.used_bytes');
  const plan = query.data.metrics.find((m) => m.key === 'storage.bucket.capacity_bytes');
  if (!used) return <>Space used not measured yet</>;
  if (!plan) return <>{formatStorageSize(used.value)} used</>;
  return (
    <>
      {formatStorageSize(used.value)} / {formatStorageSize(plan.value)} (
      {storagePercent(used.value, plan.value)}%) · {storagePercent(used.value, plan.value, true)}%
      left
    </>
  );
}
